import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Link,
  List,
  ListItemButton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { SxProps } from '@mui/system'
import get from 'lodash/get'
import {
  FC,
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import YouTube from 'react-youtube'
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from 'swiper/react'
import { IconButton, Icons, InformationModal } from 'utoniq-ui'
import { InlineMenu } from 'utoniq-ui/src/components/Input/InlineMenu'

import {
  Maybe,
  MembershipInterval,
  PostBoxFragment,
  PostStatus,
  PostType,
  ResourceFragment,
} from '@/clients/utoniq-core/schema'
import { ThumbnailFullScreenModal } from '@/components/organisms'
import { SoundPurePlayer } from '@/components/organisms/resourcePlayer/SoundPlayer/SoundPurePlayer'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Organism } from '@/core/architecture'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'
import { usePlayingResource } from '@/core/resources'
import { filterHiddenItem } from '@/lib/array'
import { dayjs } from '@/lib/dayjs'
import { usePrivateAction } from '@/lib/privateAction'

import { HtmlRenderer } from '../HtmlRenderer'
import { ThumbnailImage } from '../ThumbnailImage'

type ArrowProps = {
  onClick?: () => void
}

export type PostBoxProps = {
  post: PostBoxFragment
  fullWidth?: boolean
  loadingDeletePost?: boolean
  sx?: SxProps
  clickable?: boolean
  fullContent?: boolean
  isConsole?: boolean
  userId?: string
  mediaProps?: {
    sx?: SxProps
  }
  isMembershipPersonalInformationRequired?: boolean
  enableEmbedHtml?: boolean
  responsive?: boolean
  detail?: boolean
  onLike?: () => void
  onReply?: () => void
  onReportArtist?: () => Promise<void>
  onBlockArtist?: () => Promise<void>
  onDeletePost?: (id: string) => Promise<void>
  onClickShare?: () => Promise<void>
}

export type PostDetailCellProps = PropsWithChildren<{
  postId: string
  planId?: string
  planTitle?: string
  isAccessible: boolean
  isShowModalPersonalInfoRequire?: boolean
  artist: PostBoxFragment['artist']
  clickable: PostBoxProps['clickable']
  isConsole: PostBoxProps['isConsole']
  userId?: PostBoxProps['userId']
}>

export const PostBox: FC<PostBoxProps> = ({
  post: p,
  fullWidth,
  clickable = true,
  sx,
  mediaProps,
  fullContent,
  onLike,
  onReply,
  isMembershipPersonalInformationRequired,
  responsive = false,
  detail = false,
  onReportArtist,
  onBlockArtist,
  isConsole,
  onDeletePost,
  loadingDeletePost,
  userId,
  enableEmbedHtml = false,
  onClickShare,
}) => {
  const { showMessage, showError } = useMessage()
  const { handleLoginModal } = usePrivateAction()
  const { routerPages, generatePath } = useRouter()
  const [open, setOpen] = useState<boolean>(false)
  const { t } = useTranslation('postDetail')
  const [openInformationModal, setOpenInformationModal] =
    useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const mdUp = useMediaQuery<Theme>(theme => theme.breakpoints.up('md'))
  const isShowModalPersonalInfoRequire = useMemo(() => {
    if (!p.isPremium) {
      return false
    }
    return (
      isMembershipPersonalInformationRequired &&
      p.artist?.patron?.isPersonalInformationRequired
    )
  }, [
    isMembershipPersonalInformationRequired,
    p.artist?.patron?.isPersonalInformationRequired,
    p.isPremium,
  ])
  /**
   * @description
   * コメント機能を無効化しているか否か
   * 無効化している場合はリプライのトグルボタンを表示しない
   */
  const disableReplies = p.artist.settings?.features?.post?.disableReplies
  /**
   * @description
   * いいね機能を無効化しているか否か
   * 無効化している場合はいいねボタンを表示しない
   */
  const disableLikes = p.artist.settings?.features?.post?.disableLikes

  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setOpen(open)
    },
    []
  )

  const actions = [
    {
      title: t('report'),
      async onClick() {
        if (!onReportArtist) {
          return
        }
        await onReportArtist()
        setOpen(false)
      },
      divider: false,
    },
    {
      title: t('block'),
      hidden: config.applicationType === 'standalone',
      async onClick() {
        if (!onBlockArtist) {
          return
        }
        await onBlockArtist()
        setOpen(false)
      },
      divider: true,
    },
    {
      title: t('cancel'),
      onClick: toggleDrawer(false),
      divider: false,
      titleColor: 'primary.main',
    },
  ]

  return (
    <>
      <Card
        component={Stack}
        key={p.id}
        sx={{
          display: 'flex',
          width: fullWidth || responsive ? '100%' : 315,
          minHeight: responsive ? 356 : undefined,
          height: 'auto',
          ...sx,
        }}
      >
        <PostDetailCell
          postId={p.id}
          planId={p.planId ?? ''}
          clickable={clickable}
          isAccessible={p.isAccessible}
          artist={p.artist}
          isConsole={isConsole}
          isShowModalPersonalInfoRequire={!!isShowModalPersonalInfoRequire}
          planTitle={p.plan?.planTitle ?? ''}
        >
          {((p.isAccessible && p.resourceType !== PostType.Image) ||
            !p.isAccessible ||
            p.resourceType === PostType.Image) && (
            <PostMedia
              clickable={clickable}
              {...mediaProps}
              post={p}
              isConsole={isConsole}
              responsive={responsive}
              detail={detail}
            />
          )}
          {p.status === PostStatus.Scheduled && !p.isStory && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ px: 4, pt: 5 }}
            >
              <Icons.Calendar
                fontSize="xSmall"
                sx={{ color: 'primary.main' }}
              />
              <Typography
                variant="overline"
                color="primary"
                sx={{ textTransform: 'unset' }}
              >
                {t('scheduleMessage', { date: p.formattedPublishedAt })}
              </Typography>
            </Stack>
          )}
          {p.isStory && isConsole && (
            <StoryStatusTag publishedAt={p.publishedAt} />
          )}
          <CardHeader
            sx={{
              pb: 2,
              color: 'inherit',
              width: '100%',
              '& .MuiCardHeader-action': {
                m: 'auto',
              },
            }}
            avatar={
              <Link
                onClick={handleLoginModal()}
                href={generatePath(routerPages.UserProfile, {
                  userId: p.artist.id,
                })}
              >
                <Avatar
                  src={p.artist.avatar?.smallUri || undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    mr: -2,
                    position: 'relative',
                    zIndex: 2,
                  }}
                />
              </Link>
            }
            onClick={(event: MouseEvent<HTMLDivElement>) => {
              event.stopPropagation()
            }}
            title={
              <Link
                underline="none"
                onClick={handleLoginModal()}
                href={generatePath(routerPages.UserProfile, {
                  userId: p.artist.id,
                })}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ position: 'relative', zIndex: 2, alignItems: 'center' }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    {p.artist.displayName}
                  </Typography>

                  <Typography variant="caption" color="text.hint">
                    {p.formattedRelativePublishedAt}
                  </Typography>
                </Stack>
              </Link>
            }
            titleTypographyProps={{
              sx: { width: 'fit-content' },
            }}
            action={
              <Stack
                flex={1}
                justifyContent="flex-end"
                direction="row"
                alignItems="center"
                spacing={2}
              >
                {p.isPremium && (
                  <Icons.MembersOnly
                    fontSize="medium"
                    sx={{ color: 'primary.main' }}
                  />
                )}
                {!clickable && !isConsole && userId !== p.artist.id && (
                  <>
                    <IconButton.More
                      size="small"
                      onClick={e => {
                        setAnchorEl(e.currentTarget)
                        toggleDrawer(true)()
                      }}
                    />
                    {responsive && mdUp && (
                      <InlineMenu
                        menuItems={filterHiddenItem(
                          actions.filter(action => action.title !== t('cancel'))
                        ).map(({ title, onClick }, index) => ({
                          label: title,
                          disabled: false,
                          onClick: () => onClick(),
                        }))}
                        open={open}
                        anchorEl={anchorEl || undefined}
                        onClose={() => toggleDrawer(false)()}
                      />
                    )}
                  </>
                )}
              </Stack>
            }
          />
          {p.category ? (
            <Stack sx={{ padding: '8px 16px 8px', width: 'fit-content' }}>
              <Typography
                variant="caption"
                color="white"
                bgcolor={'text.secondary'}
                sx={{ padding: 1, borderRadius: 1 }}
              >
                {p.category?.name}
              </Typography>
            </Stack>
          ) : (
            <></>
          )}
          <Stack flex={1} width="100%">
            <CardContent sx={{ pt: 1, pb: 2, display: 'flex', flexGrow: 1 }}>
              <Typography
                variant="body1"
                sx={
                  {
                    wordBreak: 'break-all',
                    '&':
                      fullContent || !p.isAccessible
                        ? {}
                        : {
                            ...styleSets.lineCamp(getLineCamp(p)),
                            whiteSpace: 'pre-wrap',
                            /**
                             * @description
                             * iosの一部画面のみ、特定のCSSが効かない(ex: -webkit-box-orient)
                             * hotfixとして文字数分の高さを明示的に指定し、overflow: hiddenによりはみ出た文字が隠されレイアウト崩れを防ぐ対応を入れる。
                             * https://app.clickup.com/t/908127/UT-4232
                             */
                            maxHeight: getMaxHeight(get(sx, 'height', 0), p),
                          },
                  } as SxProps
                }
              >
                <HtmlRenderer
                  text={p.content}
                  enableEmbedHtml={enableEmbedHtml}
                  tagName="form"
                />
              </Typography>
            </CardContent>
            <CardActions
              sx={{
                pt: 0,
                pb: 3,
                px: 5,
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                {isConsole && userId === p.artist.id && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={3}
                    sx={{
                      position: 'relative',
                      zIndex: 2,
                      color: 'text.secondary',
                    }}
                  >
                    <IconButton.Pencil
                      size="small"
                      href={generatePath(routerPages.ConsolePostEdit, {
                        postId: p.id,
                      })}
                    />
                    <IconButton.Trash
                      onClick={() => {
                        if (!onDeletePost) return
                        setOpenInformationModal(true)
                      }}
                      size="small"
                    />
                  </Stack>
                )}
              </Box>
              <Stack direction="row" alignItems="center" spacing={5}>
                {!disableLikes && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={
                      !(!p.isAccessible || isShowModalPersonalInfoRequire)
                        ? { position: 'relative', zIndex: 2 }
                        : {}
                    }
                  >
                    <IconButton.Like
                      disabled={
                        !p.isAccessible || !!isShowModalPersonalInfoRequire
                      }
                      onClick={handleLoginModal(e => {
                        e.stopPropagation()
                        e.preventDefault()
                        onLike && onLike()
                      })}
                      onMouseDown={e => {
                        //disable ripple on parent
                        e.stopPropagation()
                      }}
                      onTouchStart={e => {
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                      disableTouchRipple={!onLike}
                      liked={p.isLike}
                    />
                    <Typography variant="caption">{p.likeCount}</Typography>
                  </Stack>
                )}
                {!disableReplies && (
                  <Stack direction="row" alignItems="center">
                    <IconButton.Reply
                      disabled={!p.isAccessible}
                      onClick={handleLoginModal(onReply)}
                      disableTouchRipple={!onReply}
                    />
                    <Typography variant="caption">{p.replyCount}</Typography>
                  </Stack>
                )}
                {onClickShare && (
                  <Stack direction="row" alignItems="center">
                    <IconButton.Share onClick={onClickShare} />
                  </Stack>
                )}
              </Stack>
            </CardActions>
          </Stack>
        </PostDetailCell>
      </Card>
      {(!mdUp || !responsive) && (
        <Drawer
          open={open}
          onClose={toggleDrawer(false)}
          sx={{
            '& .MuiPaper-root': {
              maxWidth: responsive ? '100%' : undefined,
            },
          }}
        >
          <Box sx={{ p: 5 }}>
            <Typography variant="h6" color="grey.600" align="center">
              {p.artist.displayName}
            </Typography>
          </Box>
          <Divider />
          <List>
            {filterHiddenItem(actions).map(
              ({ title, onClick, divider, titleColor }, index) => (
                <ListItemButton
                  divider={divider}
                  key={index}
                  component={Box}
                  sx={{ height: 52, justifyContent: 'center' }}
                  onClick={handleLoginModal(onClick)}
                >
                  <Typography variant="h6" color={titleColor}>
                    {title}
                  </Typography>
                </ListItemButton>
              )
            )}
          </List>
        </Drawer>
      )}
      <InformationModal
        open={openInformationModal}
        onClose={() => setOpenInformationModal(false)}
        title={t('titleModalDelete')}
        content={t('contentModalDelete')}
        buttons={
          <Button
            sx={{ width: 212 }}
            size="large"
            loading={!!loadingDeletePost}
            onClick={async () => {
              if (!onDeletePost) return
              try {
                await onDeletePost(p.id)
                showMessage(t('theDeletionIsComplete'))
              } catch (e) {
                showError(t('anErrorHasOccurred'))
              }
              setOpenInformationModal(false)
            }}
          >
            {t('textButtonOkModalDelete')}
          </Button>
        }
        cancelText={t('textButtonCloseModalDelete')}
      />
    </>
  )
}

const PostDetailCell: FC<PostDetailCellProps> = ({
  clickable,
  postId,
  planTitle,
  artist,
  isAccessible,
  isConsole,
  children,
  planId,
  isShowModalPersonalInfoRequire,
}) => {
  const { routerPages, generatePath } = useRouter()
  const { showModal } = useSettingUserInfoModal()
  const { t } = useTranslation('postDetail')
  const [open, setOpen] = useState<boolean>(false)
  // ユーザーが加入しているメンバーシップよりも上位プランであるかどうか
  const isGraterPlan = !isAccessible && planId

  return (
    <Stack
      flex={1}
      sx={{
        width: '100%',
        position: 'relative',
        bgcolor: clickable ? 'grey.50' : 'unset',
      }}
    >
      {children}
      <CardActionArea
        disabled={!clickable}
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          zIndex: 1,
        }}
        onClick={() => {
          !isAccessible && setOpen(true)
          isShowModalPersonalInfoRequire && showModal()
        }}
        href={
          isAccessible && !isShowModalPersonalInfoRequire
            ? generatePath(
                isConsole
                  ? routerPages.ConsolePostDetail
                  : routerPages.PostDetail,
                { postId }
              )
            : ''
        }
      />
      <InformationModal
        open={open}
        onClose={() => setOpen(false)}
        title={
          isGraterPlan
            ? t('viewMemberInfoTitleWithDiff')
            : t('viewMemberInfoTitle')
        }
        content={
          isGraterPlan
            ? t('viewMemberInfoDescWithDiff', { planTitle })
            : t('viewMemberInfoDesc', { displayName: artist.displayName })
        }
        cancelText={t('close')}
        buttons={
          <Button
            size="large"
            color="primary"
            sx={{ minWidth: 212 }}
            href={
              artist?.id
                ? generatePath(routerPages.ArtistMembership, {
                    artistId: artist.id,
                  })
                : undefined
            }
          >
            {t('seeDetail')}
          </Button>
        }
      />
    </Stack>
  )
}

const PostMedia = ({
  post: {
    resourceType,
    resource,
    isAccessible,
    content,
    id,
    resources,
    planId,
    planTitle,
    videoThumbnail,
    imageThumbnail,
    isStory,
    plan,
  },
  sx,
  clickable,
  isConsole = false,
  responsive,
  detail,
}: {
  post: PostBoxFragment
  sx?: SxProps<Theme>
  clickable: boolean
  isConsole?: boolean
  responsive?: boolean
  detail?: boolean
  videoData?: PostBoxFragment[]
  videoOnFetchMore?: () => Promise<void>
  onVideoDataLikePost?: (
    postId: string,
    isLike: boolean,
    likeCount: number
  ) => Promise<void>
}) => {
  const { routerPages, generatePath } = useRouter()
  const youTubeRef = useRef<YouTube>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const swiperRef = useRef<SwiperRef>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const { playingResource, playResource } = usePlayingResource()
  const [isPlaying, setIsPlaying] = useState(false)
  const uri = resource?.uri || ''
  const imageUri = resources?.[0]?.mediumUri ?? resources?.[0]?.uri ?? ''
  const [currentResource, setCurrentResource] =
    useState<Maybe<ResourceFragment>>()
  const [videoDuration, setVideoDuration] = useState<string>('')
  const [isVideoLoading, setIsVideoLoading] = useState(true)

  // 動画の再生時間を取得
  useEffect(() => {
    if (!videoRef.current) return

    const videoElement = videoRef.current
    setIsVideoLoading(true)

    const handleLoadedMetadata = () => {
      const seconds = Math.floor(videoElement.duration || 0)
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      setVideoDuration(
        `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
      )
      setIsVideoLoading(false)
    }

    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata)

    // すでにメタデータがロード済みの場合の処理
    if (videoElement.readyState >= 1) {
      handleLoadedMetadata()
    }

    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata)
    }
  }, [videoRef.current])

  useEffect(() => {
    if (playingResource !== resource?.videoId) {
      youTubeRef.current?.getInternalPlayer()?.pauseVideo()
    }
  }, [playingResource, resource?.videoId])

  const renderVideoContent = () => {
    // プレミアムコンテンツで未購入の場合
    if (!isAccessible) {
      return (
        <>
          {isStory && <Limited24hTag sx={Limited24hTagPositionStyle} />}
          <PremiumOverlay
            planId={planId ?? ''}
            planTitle={plan?.planTitle ?? ''}
            planInterval={plan?.interval}
          />
          {videoThumbnail?.blurUri && (
            <ThumbnailImage
              src={videoThumbnail?.blurUri ?? ''}
              alt="membership only video thumbnail"
            />
          )}
        </>
      )
    }

    // 通常の動画表示
    return (
      <>
        {isStory && <Limited24hTag sx={Limited24hTagPositionStyle} />}
        {/* ローディング表示 */}
        {!isPlaying && isVideoLoading && (
          <Stack
            sx={{
              position: 'absolute',
              justifyContent: 'center',
              alignItems: 'center',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'grey.100',
              zIndex: 2,
            }}
          >
            <CircularProgress />
          </Stack>
        )}

        {/* サムネイル表示 */}
        {!isPlaying && !isVideoLoading && (
          <VideoThumbnail
            videoThumbnailUri={videoThumbnail?.uri ?? ''}
            onClick={() => {
              if (videoRef.current) {
                setIsPlaying(true)
                videoRef.current.play()
              }
            }}
            videoDuration={videoDuration}
          />
        )}

        {/* 動画プレイヤー */}
        {resource?.uri && (
          <video
            width="100%"
            height="100%"
            ref={videoRef}
            onEnded={() => setIsPlaying(false)}
            controls={isPlaying}
            style={{ display: isPlaying ? 'block' : 'none' }}
          >
            <source src={resource.uri} type="video/mp4" />
          </video>
        )}
      </>
    )
  }

  switch (resourceType) {
    case PostType.Video:
      if (!resource) {
        return null
      }
      return (
        <Box
          component={clickable && isAccessible ? Link : Box}
          href={
            isConsole
              ? generatePath(routerPages.ConsolePostDetail, { postId: id })
              : clickable && isAccessible
              ? generatePath(routerPages.PostDetail, { postId: id })
              : undefined
          }
          sx={
            {
              position: 'relative',
              width: responsive ? '100%' : undefined,
              maxHeight: detail ? 532 : 266,
              minHeight: responsive ? 160 : undefined,
              height: responsive ? undefined : 160,
              aspectRatio: responsive ? '335 / 160' : undefined,
              overflow: 'hidden',
              '& iframe': {
                pointerEvents: clickable ? 'none' : 'unset',
                display: 'block',
                height: responsive ? '100%' : 160,
                width: '100%',
                position: responsive ? 'absolute' : undefined,
                ...sx,
              },
              ...sx,
            } as SxProps
          }
        >
          {/* YouTube ポストの場合 */}
          {resource?.videoId && (
            <>
              {isStory && <Limited24hTag sx={Limited24hTagPositionStyle} />}
              <YouTube
                ref={youTubeRef}
                id={resource.videoId}
                videoId={resource.videoId}
                opts={{
                  playerVars: {
                    controls: +isAccessible ? 1 : 0,
                  },
                }}
                onPlay={() => playResource(resource?.videoId ?? '')}
              />
            </>
          )}
          {/* 通常の動画ポストの場合 */}
          {resource?.uri && renderVideoContent()}
        </Box>
      )
    case PostType.Sound:
      if (!resource) {
        return null
      }
      return (
        <Box
          sx={{
            position: 'relative',
            pt: 5,
            px: 5,
            pb: 3,
            bgcolor: 'grey.50',
            height: 160,
            ...sx,
          }}
        >
          <SoundPurePlayer uri={uri} />
          {!isAccessible && (
            <PremiumOverlay
              planId={planId ?? ''}
              planTitle={plan?.planTitle ?? ''}
              planInterval={plan?.interval}
            />
          )}
        </Box>
      )
    case PostType.Image:
      if (!imageUri && !resources?.length) {
        return null
      }
      return (
        <Box
          sx={{
            position: 'relative',
            width: responsive ? '100%' : undefined,
            maxHeight: detail ? undefined : 266,
            minHeight: responsive ? 160 : undefined,
            height: responsive ? undefined : 160,
            aspectRatio: detail ? undefined : '335 / 160',
            overflow: 'hidden',
            '.image-swiper .swiper-wrapper': {
              display: 'flex',
              alignItems: 'center',
            },
            ...sx,
          }}
        >
          {isStory && <Limited24hTag sx={Limited24hTagPositionStyle} />}
          {!isAccessible && (
            <PremiumOverlay
              planId={planId ?? ''}
              planTitle={plan?.planTitle ?? ''}
              planInterval={plan?.interval}
            />
          )}

          {clickable || !((resources?.length || 0) > 1) ? (
            <CardMedia
              component="img"
              image={
                isAccessible ? imageUri ?? '' : imageThumbnail?.blurUri ?? ''
              }
              alt={content}
              sx={{
                width: responsive ? '100%' : undefined,
                height: '100%',
                objectFit: detail ? undefined : 'cover',
              }}
            />
          ) : (
            <>
              <Swiper
                ref={swiperRef}
                loop={false}
                slidesPerView={1}
                centeredSlides
                spaceBetween={0}
                initialSlide={0}
                enabled={(resources?.length || 0) > 1 && isAccessible}
                className="image-swiper"
                onSlideChange={({ activeIndex }: SwiperClass) =>
                  setActiveIndex(activeIndex)
                }
              >
                {resources?.map(
                  ({ uri, mediumUri }, index) =>
                    uri && (
                      <SwiperSlide key={index}>
                        <Stack
                          flex={1}
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            width: '100%',
                            height: detail ? '100%' : 469,
                            bgcolor: 'grey.50',
                          }}
                          onClick={() => {
                            if (!isAccessible) return
                            setCurrentResource({ uri })
                          }}
                        >
                          {/**
                           * 表示しているのは画像コンテンツだが
                           * 他箇所との表示の整合性をとるために
                           * Avatar コンポーネントを用いているものと思われる
                           */}
                          <Avatar
                            variant="square"
                            src={mediumUri ?? uri ?? ''}
                            alt={content}
                            sx={{
                              height: detail ? '100%' : 469,
                              width: '100%',
                              img: {
                                objectFit: 'contain',
                              },
                            }}
                          />
                        </Stack>
                      </SwiperSlide>
                    )
                )}
                {isAccessible && activeIndex > 0 && (
                  <PrevArrow
                    onClick={() => swiperRef.current?.swiper.slidePrev()}
                  />
                )}
                {isAccessible && activeIndex < (resources?.length || 0) - 1 && (
                  <NextArrow
                    onClick={() => swiperRef.current?.swiper.slideNext()}
                  />
                )}
              </Swiper>

              <Box
                sx={{
                  position: 'absolute',
                  right: 12,
                  bottom: 12,
                  bgcolor: 'grey.900',
                  borderRadius: '40px',
                  zIndex: 3,
                  p: '3px 6px',
                  display: isAccessible ? 'block' : 'none',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: 'grey.50' }}>
                  {(activeIndex ?? 0) + 1}/{resources?.length}
                </Typography>
              </Box>

              <ThumbnailFullScreenModal
                resource={currentResource}
                open={!!currentResource}
                onClose={() => setCurrentResource(null)}
              />
            </>
          )}
        </Box>
      )
    default:
      return null
  }
}

const VideoThumbnail: FC<{
  videoThumbnailUri: string
  onClick?: () => void
  videoDuration?: string
}> = ({ videoThumbnailUri, onClick, videoDuration }) => {
  return (
    <Stack
      sx={{
        position: 'absolute',
        justifyContent: 'center',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `url(${videoThumbnailUri})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        cursor: 'pointer',
        zIndex: 2,
      }}
      onClick={onClick}
    >
      <Stack direction="column" spacing={1} sx={{ placeItems: 'center' }}>
        <Icons.AudioPlay
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            zIndex: 2,
            color: 'primary',
            bgcolor: 'white',
            borderRadius: '50%',
            p: 1,
            transform: 'translate(-50%, -50%)',
            fontSize: 64,
          }}
        />
      </Stack>
      {videoDuration && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            color: 'white',
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            padding: '2px 4px',
            borderRadius: 1,
          }}
        >
          {videoDuration}
        </Typography>
      )}
    </Stack>
  )
}

const PremiumOverlay = ({
  planId,
  planTitle,
  planInterval,
}: {
  planId?: string
  planTitle?: string
  planInterval?: MembershipInterval
}) => {
  const { t } = useTranslation('artistPosts')
  return (
    <Stack
      sx={{
        position: 'absolute',
        justifyContent: 'center',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'black',
        filter: 'opacity(0.72)',
        zIndex: 2,
      }}
    >
      <Stack direction="column" spacing={1} sx={{ placeItems: 'center' }}>
        <Icons.MembersOnly fontSize="medium" sx={{ color: 'white' }} />
        <Typography
          variant="h5"
          color="white"
          sx={{ textAlign: 'center', px: 5 }}
        >
          {/* 下記のチケットの対応で年額プランを指定されていた場合は、そのプラン限定にする
          API側でプランタイトル + 「年額のみ」 という文字列を返すようにしているため、そのまま表示している
          月額の場合は該当プラン以上でアクセス可能
          https://app.clickup.com/t/908127/UT-23488 */}
          {planId && planTitle
            ? planInterval === MembershipInterval.Year
              ? planTitle
              : t('isPremiumPlan', { planTitle })
            : t('isPremium')}
        </Typography>
      </Stack>
    </Stack>
  )
}

const NextArrow: FC<ArrowProps> = props => (
  <IconButton.Next
    size="large"
    sx={{
      bgcolor: 'transparent',
      position: 'absolute',
      top: 'calc(50% - 15px)',
      right: 8,
      zIndex: 2,
      svg: {
        color: 'white',
        fontSize: 64,
      },
    }}
    {...props}
  />
)

const PrevArrow: FC<ArrowProps> = props => (
  <IconButton.Prev
    size="large"
    sx={{
      bgcolor: 'transparent',
      position: 'absolute',
      top: 'calc(50% - 15px)',
      left: 8,
      zIndex: 2,
      svg: {
        color: 'white',
        fontSize: 64,
      },
    }}
    {...props}
  />
)

type StoryStatusTagProps = {
  publishedAt: string
}

/**
 * ストーリー公開状態のタグ
 * @param publishedAt 公開開始日時
 * @returns ストーリー公開状態のタグ
 */
export const StoryStatusTag: Organism<StoryStatusTagProps> = ({
  publishedAt,
}) => {
  const { t } = useTranslation('postDetail')
  const { bgcolor, color, text, hasCaption } = useMemo(() => {
    const now = dayjs()
    const publishStart = dayjs(publishedAt)
    const publishEnd = publishStart.add(24, 'hour')

    // 公開前
    if (now.isBefore(publishStart)) {
      return {
        bgcolor: 'info.light',
        color: 'text.primary',
        text: t('beforePublished'),
        hasCaption: true,
      }
      // 公開中
    } else if (now.isBefore(publishEnd)) {
      return {
        bgcolor: 'primary.main',
        color: 'white',
        text: t('duringPublished'),
        hasCaption: false,
      }
      // 公開終了
    } else {
      return {
        bgcolor: 'grey.100',
        color: 'text.secondary',
        text: t('afterPublished'),
        hasCaption: false,
      }
    }
  }, [publishedAt])

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ pt: 5, px: 4 }}
    >
      <Chip
        size="small"
        sx={{
          height: 20,
          borderRadius: '2px',
          minWidth: 40,
          bgcolor,
          '.MuiChip-label': {
            px: 1,
            py: '2px',
          },
        }}
        label={
          <Typography color={color} variant="overline" component="p">
            {text}
          </Typography>
        }
      />
      {hasCaption && (
        <Typography variant="overline" color="info.main">
          {t('storyScheduleMessage', {
            date: dayjs(publishedAt).format('YYYY年MM月DD日 HH:mm'),
          })}
        </Typography>
      )}
    </Stack>
  )
}

// 24時間限定公開のタグの配置用共通スタイル
const Limited24hTagPositionStyle: SxProps = {
  position: 'absolute',
  top: '8px',
  left: '8px',
  zIndex: 3,
}
/**
 * @description
 * 24時間限定公開のタグ
 */
export const Limited24hTag = ({ sx }: { sx?: SxProps }) => {
  const { t } = useTranslation('postDetail')
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1,
        py: '2px',
        color: 'white',
        borderRadius: 1,
        bgcolor: 'like.tap',
        width: '74px',
        ...sx,
      }}
    >
      <Icons.HourGlass sx={{ color: 'white', width: '16px', height: '16px' }} />
      <Typography variant="subtitle2" color="white">
        {t('limited24h')}
      </Typography>
    </Stack>
  )
}

/**
 * avatar height = 60px
 * action button height = 56px
 * @param height
 * @param p
 */
function getMaxHeight(height: number, p: PostBoxFragment): number {
  //height of image, video, audio
  if (p.resourceType !== PostType.Text) {
    height -= 160
  }
  //height of schedule
  if (p.status === PostStatus.Scheduled) {
    height -= 32
  }
  return height - 60 - 56
}

function getLineCamp(p: PostBoxFragment): number {
  let lineCamp = p.resourceType === PostType.Text ? 8 : 3
  if (p.status === PostStatus.Scheduled) {
    lineCamp -= 1
  }
  return lineCamp
}
